import { attachmentApi } from '@/store/services/attachment.service'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useLoaderData } from 'react-router-dom'
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from '@material-tailwind/react'
import { LinkWithQuery } from '@/shared/components/link-with-query'
import { Pill } from '@/shared/components/pill'
import { store } from '@/store'
import { AttachmentTableWithTabs } from '@/shared/components/attachments'
import { useAuthorization } from '@/hooks'
import { HasAccess } from '@permify/react-role'
import { useCreateZipAttachmentMutation } from '@/store/services/zip-attachment.service'
import { ZipDownloadRangeDialog } from '@/shared/components/zip-download-range-dialog'

export const ContractAttachments = () => {
  const { contract: currentContract } = useLoaderData()
  const { hasAccess } = useAuthorization({
    redirection: {
      enable: true
    }
  })
  const defaultSearchParams = {
    attachable_type: 'Contractor::Contract',
    attachable_id: currentContract.id
  }

  const { t } = useTranslation()

  const pillOptions = useMemo(() => {
    return [
      { label: t('contract.id'), value: currentContract.id },
      { label: t('company.name'), value: currentContract.company.name },
      { label: t('documentNumber.one'), value: currentContract.company.documentNumber }
    ]
  }, [currentContract])

  const getMore = async (searchParams) => {
    try {
      const {
        attachments = [], meta = {}
      } = await store.dispatch(
        attachmentApi.endpoints.getAttachments.initiate(`?${searchParams.toString()}`)
      ).unwrap()

      const currentPage = Number(searchParams.get('page'))
      const hasMore = currentPage < meta?.pagination?.total_pages

      return {
        attachments,
        hasMore
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    hasAccess('contract#attachments')
  }, [])

  // ---- ZIP: state and logic ----
  const [isZipDialogOpen, setIsZipDialogOpen] = useState(false)
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false)
  const [existingExportInfo, setExistingExportInfo] = useState(null)
  const [pendingZipRequest, setPendingZipRequest] = useState(null)

  const [createZipAttachment, { isLoading: isCreatingZip }] =
    useCreateZipAttachmentMutation()

  const requestZip = async ({ fromDate, toDate }, { force = false } = {}) => {
    try {
      const response = await createZipAttachment({
        attachable_type: 'Contractor::Contract',
        attachable_id: currentContract.id,
        from_date: fromDate,
        to_date: toDate,
        ...(force ? { force: true } : {})
      }).unwrap()

      if (response?.conflict && response?.existing_export && !force) {
        setExistingExportInfo(response.existing_export)
        setPendingZipRequest({ fromDate, toDate })
        setIsConflictDialogOpen(true)
        return
      }

      // Success: close all related dialogs
      setIsZipDialogOpen(false)
      setIsConflictDialogOpen(false)
      setExistingExportInfo(null)
      setPendingZipRequest(null)

    } catch (error) {
      //console.error('Error requesting ZIP', error)

      // Keep other errors with the existing behavior
    }
  }

  const handleZipConfirm = ({ fromDate, toDate }) => {
    // First request without "force"
    requestZip({ fromDate, toDate }, { force: false })
  }

  const handleConflictCancel = () => {
    setIsConflictDialogOpen(false)
    setExistingExportInfo(null)
    setPendingZipRequest(null)
  }

  const handleConflictConfirm = () => {
    if (!pendingZipRequest) return
    requestZip(pendingZipRequest, { force: true })
  }

  return (
    <Dialog size='xl' className='h-4/5' open={true}>
      <DialogHeader>Detalle de documentos subidos</DialogHeader>

      <DialogBody className='overflow-hidden' style={{ height: 'inherit' }}>
        <div className='flex gap-3 mb-5'>
          {!!pillOptions.length &&
            pillOptions.map(({ value, label }) => (
              <Pill key={value} label={label} value={value} />
            ))}
        </div>

        <AttachmentTableWithTabs
          initialSearchParams={defaultSearchParams}
          getMore={getMore}
        />
      </DialogBody>

      <DialogFooter className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        {/* Button to open the period dialog */}
        <div className='flex flex-col sm:flex-row sm:items-center gap-2'>
          <HasAccess permissions="attachment#download_batch">
            <Button
              variant='outlined'
              onClick={() => setIsZipDialogOpen(true)}
              disabled={isCreatingZip}
            >
              Descargar documentos por período
            </Button>
          </HasAccess>
        </div>

        {/* Existing buttons */}
        <div className='flex gap-3'>
          <LinkWithQuery to={generatePath('/contracts')}>
            <Button
              variant='outlined'
              color='white'
            >
              Volver
            </Button>
          </LinkWithQuery>

          <LinkWithQuery to={generatePath('/contracts/:id/attachments/upload', { id: currentContract.id })}>
            <Button>
              Añadir documento
            </Button>
          </LinkWithQuery>
        </div>
      </DialogFooter>

      {/* Range selection dialog + conflict handling */}
      <ZipDownloadRangeDialog
        open={isZipDialogOpen}
        onClose={() => setIsZipDialogOpen(false)}
        onConfirm={handleZipConfirm}
        isLoading={isCreatingZip}
        title='Descargar documentos por período'
        conflict={{
          open: isConflictDialogOpen,
          existingExport: existingExportInfo,
          isLoading: isCreatingZip,
          onCancel: handleConflictCancel,
          onConfirm: handleConflictConfirm
        }}
      />
    </Dialog>
  )
}
